import { Box, Typography } from "@mui/material";
import { useRef, useState } from "react";

// สร้าง interface TimeProps
interface TimeProps {
    value: string;  // ค่าที่เลือกเป็นรูปแบบ "HH:MM:SS"
    startHour?: string;  // เวลาเริ่มต้นในรูปแบบ "HH:MM"
    endHour?: string;    // เวลาสิ้นสุดในรูปแบบ "HH:MM"
    onChange?: (value: string) => void;  // ฟังก์ชัน callback เมื่อเวลาที่เลือกเปลี่ยนแปลง
}

export default function Time({ value, startHour = "08:00", endHour = "18:00", onChange }: TimeProps) {
    // แปลงเวลาที่เป็นรูปแบบ "HH:MM" เป็นชั่วโมงและนาที
    const parseTime = (timeStr: string) => {
        const [hour, minute] = timeStr.split(":").map(Number);
        return { hour, minute };
    };

    const startTime = parseTime(startHour);
    const endTime = parseTime(endHour);
    const selectedTime = parseTime(value.split(":").slice(0, 2).join(":"));

    // สร้างรายการชั่วโมงทั้งหมดในวัน
    const hours = Array.from({ length: 24 }, (_, i) =>
        i.toString().padStart(2, '0')
    );

    // สร้างรายการนาทีทั้งหมดที่เป็นช่วง 5 นาที
    const minutes = Array.from({ length: 60 / 5 }, (_, i) =>
        (i * 5).toString().padStart(2, '0')
    );

    const [selectedHour, setSelectedHour] = useState(selectedTime.hour?.toString().padStart(2, '0'));
    const [selectedMinute, setSelectedMinute] = useState(selectedTime.minute?.toString().padStart(2, '0'));

    const hoursRef = useRef<HTMLDivElement>(null);
    const minutesRef = useRef<HTMLDivElement>(null);

    const handleHourClick = (hour: string, index: number) => {
        if (!isDisabledHour(hour)) {
            setSelectedHour(hour);
            setSelectedMinute('00');
            const container = hoursRef.current;
            if (container) {
                const itemHeight = container.scrollHeight / 24;
                container.scrollTo({ top: index * itemHeight, behavior: 'smooth' });
            }
            updateTime(hour, '00');
        }
    };

    const handleMinuteClick = (minute: string, index: number) => {
        if (!isDisabledMinute(minute)) {
            setSelectedMinute(minute);
            const container = minutesRef.current;
            if (container) {
                const itemHeight = container.scrollHeight / 12;
                container.scrollTo({ top: index * itemHeight, behavior: 'smooth' });
            }
            updateTime(selectedHour, minute);
        }
    };

    const updateTime = (hour: string, minute: string) => {
        if (hour && minute) {
            const newValue = `${hour}:${minute}:00`;
            if (onChange) {
                onChange(newValue);
            }
        }
    };

    const isDisabledHour = (hour: string) => {
        const hourNumber = parseInt(hour, 10);
        return hourNumber < startTime.hour || hourNumber > endTime.hour;
    };

    const isDisabledMinute = (minute: string) => {
        const hourNumber = parseInt(selectedHour, 10);
        const minuteNumber = parseInt(minute, 10);

        if (hourNumber < startTime.hour || hourNumber > endTime.hour) {
            return true;
        }

        if (hourNumber === startTime.hour && minuteNumber <= startTime.minute) {
            return true;
        }

        if (hourNumber === endTime.hour && minuteNumber > endTime.minute) {
            return true;
        }

        return false;
    };

    return (
        <Box display="flex" flexDirection="row" bgcolor="white" borderRadius='4px' width="148px" border={1} borderColor='#E5E5E5' sx={{ zIndex: 1000000 }}>
            <Box ref={hoursRef} width={74} overflow='scroll' height={216} borderRight={1} borderColor='#E5E5E5' sx={{ zIndex: 2500 }}>
                {hours.map((hour, index) => (
                    <Typography
                        key={index}
                        align="center"
                        paddingY="3px"
                        onClick={() => handleHourClick(hour, index)}
                        // ref={(el: any) => hourItemsRef.current[index] = el}
                        role="button"
                        tabIndex={0}
                        fontSize={14}
                        sx={{
                            '&:hover': {
                                backgroundColor: !isDisabledHour(hour) && hour === selectedHour ? '#EBF2F0' : '#F0F0F0',
                            },
                            cursor: isDisabledHour(hour) ? 'not-allowed' : 'pointer',
                            color: isDisabledHour(hour) ? '#BBBBBB' : 'black',
                            backgroundColor: !isDisabledHour(hour) && hour === selectedHour ? '#EBF2F0' : 'inherit',
                            pointerEvents: isDisabledHour(hour) ? 'none' : 'auto',
                        }}>
                        {hour}
                    </Typography>
                ))}
            </Box>
            <Box ref={minutesRef} width={74} overflow='scroll' height={216}>
                {minutes.map((minute, index) => (
                    <Typography
                        key={index}
                        align="center"
                        paddingY="3px"
                        onClick={() => handleMinuteClick(minute, index)}
                        // ref={(el: any) => (minuteItemsRef.current[index] = el)}
                        role="button"
                        fontSize={14}
                        tabIndex={0}
                        sx={{
                            '&:hover': {
                                backgroundColor: !isDisabledMinute(minute) && minute === selectedMinute ? '#EBF2F0' : '#F0F0F0',
                            },
                            cursor: isDisabledMinute(minute) ? 'not-allowed' : 'pointer',
                            color: isDisabledMinute(minute) ? '#BBBBBB' : 'black',
                            backgroundColor: !isDisabledMinute(minute) && minute === selectedMinute ? '#EBF2F0' : 'inherit',
                            pointerEvents: isDisabledMinute(minute) ? 'none' : 'auto',
                        }}>
                        {minute}
                    </Typography>
                ))}
            </Box>
        </Box>
    );
}
